import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Gamepad2,
  ArrowRight,
  Mail,
  Sparkles,
  Download,
  ArrowUpRight,
  ShoppingBag,
  Smartphone,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/54jbluz3_IMG-20260904-WA0003.jpg";

// Toggle to bring the games/studio sections back on the public site.
const SHOW_GAMES = true;
// Toggle to show the merch/apparel shop on the public site.
const SHOW_MERCH = false;

const SCREENSHOTS = [
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/gol847s7_screenshot%201.jpg", caption: "Tank combat in the streets" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/j7jkct2j_screenshot%202.jpg", caption: "Confronting rogue machines" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/rp5bpni3_screenshot%205.jpg", caption: "Rooftop perspective over the city" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/u6a0esrt_screenshot%206.jpg", caption: "Drive vehicles across the world" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/nvpalxc3_screenshot%203.jpg", caption: "Military humvee in a burning intersection" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/tv969rux_screenshot%204.jpg", caption: "Explosive moments across the city" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/85u0z478_screenshot%207.jpg", caption: "Story beats inside a fractured city" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/92hh1140_screenshot%208.jpg", caption: "Escape through robot-controlled streets" },
];

const CAPELLA_SHOTS = [
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/jymnp72o_ss_148a0add661b763d71cd885be694d2bbb40d1a23.1920x1080.jpg", caption: "Get in the car. Mission start" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/xb4bzqq6_ss_f868ce78518f3b601c518e7b0793ee9a271d87ea.1920x1080.jpg", caption: "Take out the enemies" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/wgjsa71z_ss_1caa02269f4a814a39c9c2a4f9e9579acbb439d9.1920x1080.jpg", caption: "Shoot at the barriers behind the fire" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/elom28qp_ss_d499739416add7c4786ce0405eb4e64e5057298a.1920x1080.jpg", caption: "Indoor firefights and demolition" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/vy8c4w6c_ss_7ce663b0082f62815cc64687e6260d707be5e0ce.1920x1080.jpg", caption: "Destroy the chemical convoy" },
  { src: "https://customer-assets.emergentagent.com/job_gaming-portfolio-13/artifacts/lv0n52ul_ss_59fbd60f2abe038767c561260603a297a52775a4.1920x1080.jpg", caption: "Collect packages in the countryside" },
];

const MERCH = [
  {
    id: "long-sleeve-tee",
    name: "Andromis Long Sleeve Tee",
    kind: "Long Sleeve",
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/55n6jyrq_865e9113-d330-42c6-bcb3-65e5127f8423.png",
    href: "https://andromis-2.myshopify.com/products/andromis-games-womens-long-sleeve-jersey-t-shirt-bella-canvas-b6500",
    accent:
      "A fitted long-sleeve cut in soft cotton. Layer-ready for cooler days and clean enough for daily wear.",
  },
  {
    id: "tshirt",
    name: "Andromis Gaming T-Shirt",
    kind: "T-Shirt",
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/tbdxfkgm_83fcfd39-f613-437b-a406-1698cab6ad64.png",
    href: "https://andromis-2.myshopify.com/products/andromis-games-womens-heavy-cotton-t-shirt-gildan-g500l",
    accent:
      "Stay comfortable while showing your passion for gaming. Modern esports-inspired design with lightweight, breathable fabric. Perfect for gaming, streaming, and everyday wear.",
  },
  {
    id: "cropped-hoodie",
    name: "Andromis Cropped Hoodie",
    kind: "Cropped Hoodie",
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/paxmic6x_8c6bfc42-2bae-4139-a6f0-a71bab578bf2.png",
    href: "https://andromis-2.myshopify.com/products/andromis-games-womens-lightweight-crop-hoodie-independent-trading-co-afx64crp",
    accent:
      "A cropped, relaxed hoodie in heavyweight fleece. Bold Andromis crest front and center, built for lounge and street.",
  },
  {
    id: "hoodie",
    name: "Andromis Games Premium Pullover Hoodie",
    kind: "Hoodie",
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/8ebtg31g_d593425a-7c56-49a3-afd4-e05fb8e61065.png",
    href: "https://andromis-2.myshopify.com/products/andromis-games-premium-pullover-hoodie-hanes-p170-ecosmart-unisex",
    accent:
      "Represent your passion for gaming. A bold esports-inspired design delivering warmth, comfort, and style for gaming sessions, casual wear, and everyday adventures.",
  },
  {
    id: "sweater",
    name: "Andromis Games Premium Crewneck Sweatshirt",
    kind: "Sweatshirt",
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/3gsj57az_b4ea8f71-c0de-44a0-b6ff-097daf7f0b42.png",
    href: "https://andromis-2.myshopify.com/products/andromis-games-premium-crewneck-sweatshirt-hanes-p1607-ecosmart-unisex",
    accent:
      "Stay warm and game in style. A sleek gaming-inspired look combined with a soft, premium feel. Built for comfort and everyday use.",
  },
  {
    id: "classic-tee",
    name: "Andromis Games Heavyweight Gamer T-Shirt",
    kind: "T-Shirt",
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/xyc2c44p_c5ac677e-5e0d-46d8-8be8-9fb82a675b63.png",
    href: "https://andromis-2.myshopify.com/products/andromis-games-heavyweight-gamer-t-shirt-hanes-5180-beefy-t-unisex",
    accent:
      "A relaxed, oversized crew-neck tee in premium heavyweight cotton. The Andromis crest front and center, built for everyday rotation.",
  },
];

const CHAPTERS = [
  {
    n: "00",
    title: "Independent by design.",
    body: "Andromis Games LLC is a small, independent studio. We make focused, single-player adventures and design apparel that carries the same identity. No microtransactions. No games-as-a-service dependency. A complete game at launch.",
  },
  {
    n: "01",
    title: "Smaller worlds, deeper interaction.",
    body: "We believe in tight, meaningful worlds over oversized ones. Cities where civilians feel alive, where every action ripples, where every system exists for a reason.",
  },
  {
    n: "02",
    title: "Craft over scale.",
    body: "Our flagship project, Morlen, is already playable as a PC prototype. Capella, our first released title, is live now on Steam. Every step is intentional, every release earned.",
  },
];

const GAMEVERSE_SHOTS = [
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/a7kspbz8_04_bulls_and_cows_complete.png",
    caption: "Level complete · stars, time, best score",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/zmxb7i98_01_guess_the_word_action.png",
    caption: "Guess the Word · 900 definitions",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/eqwta3dg_02_hangman_action.png",
    caption: "Hangman · hints and lifelines",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/mgm9ys2j_03_trivia_action.png",
    caption: "Trivia · themed question packs",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/yp4ytfdu_04_bulls_and_cows_action.png",
    caption: "Bulls & Cows · logic and deduction",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/yrjjmvto_05_number_recall_complete.png",
    caption: "Number Recall · level complete",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/qfjn7bpn_06_word_recall_action.png",
    caption: "Word Recall · type mode",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/6ewj2zuy_06_word_recall_complete.png",
    caption: "Word Recall · perfect accuracy",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/8k3ftooy_07_color_sequence_action.png",
    caption: "Color Sequence · memory challenge",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/9dfmdmym_05_number_recall_action.png",
    caption: "Number Recall · remember the number",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/xh8lhvly_10_memory_action.png",
    caption: "Memory Pairs · match the icons",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/fk3tku8f_10_memory_complete.png",
    caption: "Memory Pairs · perfect run",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/1p3ybsy6_07_color_sequence_complete.png",
    caption: "Color Sequence · well played",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/teql8zt0_08_search_word_action.png",
    caption: "Word Search · find them in the grid",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/yj55a9g9_09_sliding_puzzle_action.png",
    caption: "Sliding Puzzle · solve the image",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/1on00cq5_14_chess_action.png",
    caption: "Chess · play against the AI",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/v5keb23o_11_color_connect_action.png",
    caption: "Color Connect · link matching dots",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/64ahl67m_12_checkers_action.png",
    caption: "Checkers · classic AI duel",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/6l9wk2hg_13_dots_and_boxes_action.png",
    caption: "Dots & Boxes · claim the grid",
  },
];

/* Masked line reveal for hero */
const HeroLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

/* Realistic Android phone frame */
const AndroidFrame = ({ src, alt, small = false }) => (
  <div
    className="relative w-full aspect-[9/19.5] bg-[#0a0d13] p-[5px] shadow-[0_20px_40px_rgba(0,0,0,0.55)]"
    style={{ borderRadius: small ? 32 : 40 }}
  >
    {/* Screen */}
    <div
      className="relative overflow-hidden bg-black w-full h-full"
      style={{ borderRadius: small ? 27 : 35 }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Center hole-punch camera */}
      <div
        className="absolute top-2 left-1/2 -translate-x-1/2 rounded-full bg-black border border-white/10 z-20"
        style={{ width: small ? 8 : 10, height: small ? 8 : 10 }}
      />
    </div>
  </div>
);

/* Reveal-on-scroll wrapper */
const Reveal = ({ children, delay = 0, y = 40, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1], delay }}
  >
    {children}
  </motion.div>
);

const Landing = () => {
  const [scrolled, setScrolled] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const heroRef = useRef(null);

  // Parallax on hero logo (mouse tracking)
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 90, damping: 20 });
  const sy = useSpring(my, { stiffness: 90, damping: 20 });
  const logoRotY = useTransform(sx, [-1, 1], [-14, 14]);
  const logoRotX = useTransform(sy, [-1, 1], [10, -10]);
  const logoTx = useTransform(sx, [-1, 1], [-20, 20]);
  const logoTy = useTransform(sy, [-1, 1], [-14, 14]);

  // Scroll parallax
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHeroMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mx.set(x * 2);
    my.set(y * 2);
  };
  const onHeroLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div className="App grain relative" data-testid="landing-page">
      {/* NAV */}
      <nav
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[#04060a]/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Andromis Games" className="h-10 w-auto object-contain max-w-[160px]" />
          </a>
          <div className="hidden md:flex items-center gap-10 text-[13px]">
            {SHOW_MERCH && (
              <a href="#merch" className="text-white/55 hover:text-white transition-colors" data-testid="nav-link-merch">Shop</a>
            )}
            <a href="#gameverse" className="text-white/55 hover:text-white transition-colors" data-testid="nav-link-gameverse">GameVerse</a>
            {SHOW_GAMES && (
              <>
                <a href="#games" className="text-white/55 hover:text-white transition-colors" data-testid="nav-link-games">Games</a>
                <a href="#manifesto" className="text-white/55 hover:text-white transition-colors" data-testid="nav-link-about">Studio</a>
              </>
            )}
            <a href="#contact" className="text-white/55 hover:text-white transition-colors" data-testid="nav-link-contact">Contact</a>
          </div>
          <a href={SHOW_MERCH ? "#merch" : "#gameverse"} className="btn-ghost text-[11px] hidden sm:inline-flex" data-testid="nav-cta">
            {SHOW_MERCH ? "Shop" : "Explore"} <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </nav>

      {/* HERO */}
      <header
        id="top"
        ref={heroRef}
        onMouseMove={onHeroMove}
        onMouseLeave={onHeroLeave}
        className="relative min-h-[100svh] flex items-center overflow-hidden pt-16"
        data-testid="hero-section"
      >
        <div className="stars" />
        <motion.div
          className="orbit-ring"
          style={{
            width: "1100px",
            height: "1100px",
            left: "-260px",
            top: "10%",
            rotate: -12,
            y: heroY,
          }}
        />
        <motion.div
          className="orbit-ring"
          style={{
            width: "1600px",
            height: "1600px",
            right: "-480px",
            top: "-30%",
            rotate: 22,
            y: useTransform(scrollYProgress, [0, 1], [0, 120]),
          }}
        />

        <motion.div
          style={{ opacity: heroOpacity }}
          className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center relative z-10 pt-16 pb-24 w-full"
        >
          <div className="md:col-span-7">
            <motion.div
              className="eyebrow mb-8 flex items-center gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
              Independent Studio · Est. 2026
            </motion.div>

            <h1
              className="h-display text-[13vw] md:text-[9vw] lg:text-[7.5vw] text-white leading-[0.9] mb-8 tracking-[-0.03em]"
              data-testid="hero-title"
            >
              <HeroLine delay={0.15}>
                <span className="chrome-text">ANDROMIS</span>
              </HeroLine>
              <HeroLine delay={0.3}>
                <span className="text-white/85 italic font-light">games.</span>
              </HeroLine>
            </h1>

            <motion.p
              className="text-base md:text-lg text-white/60 max-w-xl leading-relaxed mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
              data-testid="hero-subtitle"
            >
              We craft mobile games and premium single-player experiences.
              Focused, atmospheric, built to last, not to churn.
            </motion.p>

            <motion.div
              className="flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.85 }}
            >
              <a href="#gameverse" className="btn-primary" data-testid="hero-cta-primary">
                Play GameVerse <ArrowRight className="w-4 h-4" />
              </a>
              {SHOW_GAMES && (
                <a href="#games" className="btn-ghost" data-testid="hero-cta-secondary">
                  See our games
                </a>
              )}
              <a href="#contact" className="btn-ghost" data-testid="hero-cta-contact">
                Contact
              </a>
            </motion.div>
          </div>

          <div className="md:col-span-5 flex justify-center md:justify-end">
            <motion.div
              className="relative"
              style={{ perspective: 1200 }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
            >
              <div
                className="absolute inset-0 blur-3xl opacity-60"
                style={{ background: "radial-gradient(circle, rgba(79,168,224,0.55), transparent 70%)" }}
              />
              <motion.img
                src={LOGO_URL}
                alt="Andromis Games logo"
                className="logo-glow relative w-[300px] md:w-[420px] lg:w-[500px] object-contain"
                style={{
                  rotateY: logoRotY,
                  rotateX: logoRotX,
                  x: logoTx,
                  y: logoTy,
                  transformStyle: "preserve-3d",
                }}
                data-testid="hero-logo"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 text-[10px] tracking-[0.4em] uppercase flex flex-col items-center gap-3 z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
        >
          <span>Scroll</span>
          <motion.span
            className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent"
            animate={{ scaleY: [0.4, 1, 0.4] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </header>

      {/* MARQUEE */}
      {SHOW_GAMES && (
      <div className="relative overflow-hidden border-y border-white/5 py-6 bg-[#050810]" data-testid="marquee">
        <motion.div
          className="flex whitespace-nowrap gap-16 h-display text-3xl md:text-5xl text-white/25 tracking-[-0.02em]"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 38, ease: "linear", repeat: Infinity }}
        >
          {Array.from({ length: 2 }).map((_, k) => (
            <span key={k} className="flex items-center gap-16 pr-16">
              <span>Andromis Games</span>
              <span className="text-[#4FA8E0]">✦</span>
              <span className="italic font-light text-white/40">Morlen · in dev</span>
              <span className="text-[#4FA8E0]">✦</span>
              <span>Capella · out now</span>
              <span className="text-[#4FA8E0]">✦</span>
              <span className="italic font-light text-white/40">Apparel & Games</span>
              <span className="text-[#4FA8E0]">✦</span>
              <span>No MTX. No GaaS.</span>
              <span className="text-[#4FA8E0]">✦</span>
            </span>
          ))}
        </motion.div>
      </div>
      )}

      {/* GAMEVERSE */}
      <section
        id="gameverse"
        className="relative py-28 md:py-40 px-6 md:px-10 border-t border-white/5 overflow-hidden"
        data-testid="gameverse-section"
      >
        {/* Ambient blobs */}
        <div className="absolute top-20 left-[-10%] w-[600px] h-[600px] rounded-full opacity-[0.06] blur-3xl bg-[#4FA8E0] pointer-events-none" />
        <div className="absolute bottom-0 right-[-15%] w-[700px] h-[700px] rounded-full opacity-[0.05] blur-3xl bg-[#22c55e] pointer-events-none" />

        <div className="max-w-[1400px] mx-auto relative z-10">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-10 mb-16">
              <div className="lg:col-span-7">
                <div className="eyebrow mb-4 flex items-center gap-3">
                  <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
                  Android · Available Now
                </div>
                <h2 className="h-display text-6xl md:text-8xl lg:text-9xl text-white tracking-[-0.04em] leading-[0.9]" data-testid="gameverse-title">
                  <span className="chrome-text">Game</span>
                  <span className="italic font-light text-white/70">verse.</span>
                </h2>
                <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-snug">
                  One app. A whole universe of games.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col justify-end gap-5">
                <p className="text-white/60 leading-relaxed text-sm md:text-base">
                  GameVerse is an all-in-one gaming platform packed with fun,
                  challenging, and addictive games designed for players of all
                  skill levels. Jump between puzzle, strategy, memory, trivia,
                  arcade, and skill-based experiences without needing to download
                  a separate app for every game.
                </p>
                <p className="text-white/50 leading-relaxed text-sm md:text-base">
                  Complete hundreds of levels, challenge your mind, improve your
                  skills, earn progress, and master each game at your own pace.
                  With a constantly expanding collection of games and challenges,
                  there’s always something new to play.
                </p>
                <p className="text-white/40 leading-relaxed text-sm md:text-base italic">
                  Whether you have a few minutes to spare or want to work your
                  way through an entire campaign, GameVerse puts a growing
                  arcade right at your fingertips.
                </p>
                <p className="h-display text-white/85 text-base tracking-[0.04em]">
                  Play. Progress. Master them all.
                </p>

                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.andromisgames.arcade"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group inline-flex items-center gap-3 px-6 py-3.5 md:px-7 md:py-4 rounded-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500 bg-[length:200%_100%] hover:bg-[position:100%_0%] text-emerald-950 h-display text-sm md:text-base font-bold tracking-[0.06em] uppercase shadow-[0_15px_40px_rgba(16,185,129,0.45),inset_0_1px_0_rgba(255,255,255,0.5)] hover:shadow-[0_20px_50px_rgba(16,185,129,0.6)] hover:-translate-y-0.5 transition-all duration-500"
                    data-testid="gameverse-play-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                      <path d="M5.26 4.57l6.24 6.24-6.24 6.24c-.34-.29-.55-.72-.55-1.2V5.77c0-.48.21-.91.55-1.2zm12.52 3.36l-2.79 1.61-2.62-2.62 5.41 1.01zm-2.79 6.63l2.79 1.61-5.41 1.01 2.62-2.62zm4.03-2.32l-2.19 1.27-2.35-2.35 2.35-2.35 2.19 1.27c.63.36.63 1.27 0 1.63l-.19.11z" />
                    </svg>
                    Get it on Google Play
                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-500" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          {/* STATIC GRID (no animations) */}
          <Reveal delay={0.1}>
            <div
              className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6"
              data-testid="gameverse-grid"
            >
              {GAMEVERSE_SHOTS.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightbox(s)}
                  className="group relative w-full focus:outline-none"
                  aria-label={s.caption}
                  data-testid={`gameverse-shot-${i}`}
                >
                  <AndroidFrame src={s.src} alt={s.caption} small />
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-12 flex items-center gap-4">
              <div className="hairline flex-1 opacity-40" />
              <p className="text-white/40 text-xs tracking-[0.28em] uppercase">
                Tap any screen to view · Now available on Google Play
              </p>
              <div className="hairline flex-1 opacity-40" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* GAMES SECTION */}
      {SHOW_GAMES && (
      <section id="games" className="relative py-28 md:py-40 px-6 md:px-10" data-testid="games-section">
        <div className="max-w-[1400px] mx-auto relative z-10">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
              <div>
                <div className="eyebrow mb-4 flex items-center gap-3">
                  <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
                  Also, we make games
                </div>
                <h2 className="h-display text-5xl md:text-7xl text-white tracking-[-0.03em]" data-testid="games-title">
                  Our <span className="italic font-light text-white/70">projects.</span>
                </h2>
              </div>
              <p className="text-white/50 max-w-md text-sm leading-relaxed">
                Two projects, one philosophy: meaningful worlds, real consequences, no shortcuts.
              </p>
            </div>
          </Reveal>

          <Tabs defaultValue="morlen" className="w-full" data-testid="games-tabs">
            <TabsList className="tabs-list-andromis bg-transparent p-0 gap-3 mb-10 h-auto" data-testid="games-tabs-list">
              <TabsTrigger
                value="morlen"
                className="data-[state=active]:bg-transparent border border-white/10 rounded-full px-6 py-2.5 text-white/65 hover:text-white h-display text-sm tracking-[0.18em] uppercase"
                data-testid="tab-morlen"
              >
                <Gamepad2 className="w-3.5 h-3.5 mr-2" /> Morlen
              </TabsTrigger>
              <TabsTrigger
                value="capella"
                className="data-[state=active]:bg-transparent border border-white/10 rounded-full px-6 py-2.5 text-white/65 hover:text-white h-display text-sm tracking-[0.18em] uppercase"
                data-testid="tab-capella"
              >
                <Sparkles className="w-3.5 h-3.5 mr-2" /> Capella
              </TabsTrigger>
            </TabsList>

            {/* MORLEN */}
            <TabsContent value="morlen" className="mt-0" data-testid="morlen-content">
              <Reveal>
                <div className="glass rounded-2xl p-6 md:p-10 lg:p-14">
                  <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                      <Badge
                        variant="outline"
                        className="bg-[#4FA8E0]/10 border-[#4FA8E0]/40 text-[#9bd1f5] mb-5"
                        data-testid="morlen-badge-status"
                      >
                        Playable PC Prototype · In Development
                      </Badge>
                      <div className="mb-6">
                        <a
                          href="https://drive.google.com/file/d/1SqxfEDNtu--kuWDoNwrvuBfNC0II2euY/view?usp=drive_link"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary"
                          data-testid="morlen-download-btn"
                        >
                          <Download className="w-4 h-4" />
                          Download Prototype
                        </a>
                      </div>
                      <h3 className="h-display text-5xl md:text-6xl text-white mb-4 tracking-[-0.02em]" data-testid="morlen-title">
                        MORLEN
                      </h3>
                      <p className="text-[#9bd1f5] text-sm tracking-[0.18em] uppercase mb-6">
                        Third-person Adventure
                      </p>
                      <p className="text-white/70 leading-relaxed mb-5" data-testid="morlen-desc-1">
                        A living, reactive city threatened by rogue machines. Civilians still live their lives, even as the world around them quietly breaks apart.
                      </p>
                      <p className="text-white/55 leading-relaxed mb-8" data-testid="morlen-desc-2">
                        Your actions ripple through missions, civilians and entire districts of the city. This isn’t an empty post-apocalypse. It’s a human city under machine pressure, partly normal, partly broken, increasingly threatened.
                      </p>

                      <div className="flex flex-wrap gap-6 text-sm">
                        <div>
                          <div className="text-white/40 uppercase tracking-[0.2em] text-[10px] mb-1">Platforms</div>
                          <div className="text-white/85">PC</div>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-5">
                      <div
                        className="relative rounded-xl overflow-hidden border border-white/10 aspect-[4/5] gallery-card"
                        data-testid="morlen-hero-image"
                      >
                        <img src={SCREENSHOTS[1].src} alt="Morlen gameplay" className="gallery-img w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#04060a] via-[#04060a]/30 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="text-[10px] uppercase tracking-[0.3em] text-[#9bd1f5] mb-1">From the playable build</div>
                          <div className="text-white/90 text-sm">{SCREENSHOTS[1].caption}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-12">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="eyebrow">Gameplay Stills</div>
                      <div className="hairline flex-1" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="morlen-gallery">
                      {SCREENSHOTS.map((s, i) => (
                        <motion.button
                          key={i}
                          type="button"
                          onClick={() => setLightbox(s)}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ duration: 0.7, delay: i * 0.05, ease: [0.2, 0.7, 0.2, 1] }}
                          className="gallery-card relative rounded-lg overflow-hidden border border-white/10 aspect-square cursor-zoom-in text-left focus:outline-none focus:ring-2 focus:ring-[#4FA8E0]/60"
                          data-testid={`morlen-gallery-item-${i}`}
                        >
                          <img src={s.src} alt={s.caption} className="gallery-img w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#04060a]/90 via-transparent to-transparent" />
                          <div className="absolute bottom-2 left-3 right-3 text-[11px] text-white/75 leading-tight">
                            {s.caption}
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            </TabsContent>

            {/* CAPELLA */}
            <TabsContent value="capella" className="mt-0" data-testid="capella-content">
              <Reveal>
                <div className="glass rounded-2xl p-6 md:p-10 lg:p-14">
                  <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                      <Badge
                        variant="outline"
                        className="bg-[#4FA8E0]/10 border-[#4FA8E0]/40 text-[#9bd1f5] mb-5"
                        data-testid="capella-badge-status"
                      >
                        Available Now
                      </Badge>
                      <div className="mb-6">
                        <a
                          href="https://store.steampowered.com/app/4009560/Capella/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary"
                          data-testid="capella-steam-btn"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                            <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949-.26-.629-.75-1.121-1.378-1.383-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012z" />
                          </svg>
                          Play on Steam
                        </a>
                      </div>
                      <h3 className="h-display text-5xl md:text-6xl text-white mb-4 tracking-[-0.02em]" data-testid="capella-title">
                        CAPELLA
                      </h3>
                      <p className="text-[#9bd1f5] text-sm tracking-[0.18em] uppercase mb-6">
                        Third-person Action
                      </p>
                      <p className="text-white/70 leading-relaxed mb-5" data-testid="capella-desc-1">
                        Capella is a third-person game where you play as a soldier assigned multiple tasks, driving cars, taking out enemies and pushing through tight mission objectives.
                      </p>
                      <p className="text-white/55 leading-relaxed mb-8" data-testid="capella-desc-2">
                        A short, focused experience with a total of 5 missions. Capella is more the beginning of a larger journey than the destination, a free test run, and the first of many adventures to come from Andromis Games.
                      </p>

                      <div className="flex flex-wrap gap-6 text-sm">
                        <div>
                          <div className="text-white/40 uppercase tracking-[0.2em] text-[10px] mb-1">Platforms</div>
                          <div className="text-white/85">PC</div>
                        </div>
                        <div className="w-px bg-white/10 self-stretch" />
                        <div>
                          <div className="text-white/40 uppercase tracking-[0.2em] text-[10px] mb-1">Release Date</div>
                          <div className="text-white/85">Oct 9, 2025</div>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-5">
                      <div
                        className="relative rounded-xl overflow-hidden border border-white/10 aspect-[4/5] gallery-card"
                        data-testid="capella-hero-image"
                      >
                        <img src={CAPELLA_SHOTS[0].src} alt="Capella gameplay" className="gallery-img w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#04060a] via-[#04060a]/30 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="text-[10px] uppercase tracking-[0.3em] text-[#9bd1f5] mb-1">From the released build</div>
                          <div className="text-white/90 text-sm">{CAPELLA_SHOTS[0].caption}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-12">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="eyebrow">Gameplay Stills</div>
                      <div className="hairline flex-1" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="capella-gallery">
                      {CAPELLA_SHOTS.map((s, i) => (
                        <motion.button
                          key={i}
                          type="button"
                          onClick={() => setLightbox(s)}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ duration: 0.7, delay: i * 0.06, ease: [0.2, 0.7, 0.2, 1] }}
                          className="gallery-card relative rounded-lg overflow-hidden border border-white/10 aspect-square cursor-zoom-in text-left focus:outline-none focus:ring-2 focus:ring-[#4FA8E0]/60"
                          data-testid={`capella-gallery-item-${i}`}
                        >
                          <img src={s.src} alt={s.caption} className="gallery-img w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#04060a]/90 via-transparent to-transparent" />
                          <div className="absolute bottom-2 left-3 right-3 text-[11px] text-white/75 leading-tight">
                            {s.caption}
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      )}

      {/* MANIFESTO / STUDIO */}
      {SHOW_GAMES && (
      <section
        id="manifesto"
        className="relative py-28 md:py-40 px-6 md:px-10 border-t border-white/5 overflow-hidden"
        data-testid="about-section"
      >
        <div className="absolute -left-40 top-20 w-[500px] h-[500px] rounded-full opacity-[0.07] blur-3xl bg-[#4FA8E0] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="mb-16 md:mb-24">
              <div className="eyebrow mb-5 flex items-center gap-3">
                <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
                The Studio · Manifesto
              </div>
              <h2 className="h-display text-5xl md:text-7xl lg:text-8xl text-white tracking-[-0.03em] leading-[0.95]" data-testid="about-title">
                A small studio,
                <br />
                <span className="italic font-light text-white/55">building with intent.</span>
              </h2>
            </div>
          </Reveal>

          <div className="space-y-10 md:space-y-16 max-w-4xl">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <article className="grid md:grid-cols-12 gap-8 items-start group" data-testid={`chapter-${c.n}`}>
                  <div className="md:col-span-2 flex md:justify-start">
                    <div className="h-display text-5xl md:text-6xl text-[#4FA8E0]/80 tracking-tight leading-none">
                      {c.n}
                    </div>
                  </div>
                  <div className="md:col-span-10">
                    <h3 className="h-display text-3xl md:text-4xl text-white mb-4 tracking-[-0.02em]">
                      {c.title}
                    </h3>
                    <p className="text-white/60 leading-relaxed text-base md:text-lg max-w-3xl">
                      {c.body}
                    </p>
                    <div className="hairline mt-8 opacity-40" />
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* MERCH */}
      {SHOW_MERCH && (
      <section
        id="merch"
        className="relative py-28 md:py-40 px-6 md:px-10 border-t border-white/5 overflow-hidden"
        data-testid="merch-section"
      >
        <div className="max-w-[1400px] mx-auto relative z-10">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
              <div>
                <div className="eyebrow mb-4 flex items-center gap-3">
                  <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
                  The Shop
                </div>
                <h2 className="h-display text-5xl md:text-7xl text-white tracking-[-0.03em] leading-[0.95]" data-testid="merch-title">
                  Wear the <span className="italic font-light text-white/60">signal.</span>
                </h2>
              </div>
              <div className="flex flex-col items-start md:items-end gap-4 max-w-sm">
                <p className="text-white/50 text-sm leading-relaxed">
                  Official Andromis apparel. Hex-panel construction, chrome-and-blue crest, engineered for daily wear.
                </p>
                <a
                  href="https://andromis-2.myshopify.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative group inline-flex items-center gap-3 px-8 py-4 md:px-10 md:py-5 rounded-full bg-gradient-to-r from-[#4FA8E0] via-[#6bb8ea] to-[#4FA8E0] bg-[length:200%_100%] hover:bg-[position:100%_0%] text-[#04060a] h-display text-base md:text-lg font-bold tracking-[0.06em] uppercase shadow-[0_15px_40px_rgba(79,168,224,0.45),inset_0_1px_0_rgba(255,255,255,0.5)] hover:shadow-[0_20px_50px_rgba(79,168,224,0.6)] hover:-translate-y-1 transition-all duration-500"
                  data-testid="merch-shop-btn"
                >
                  <span className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl bg-[#4FA8E0]/60 -z-10" />
                  <ShoppingBag className="w-5 h-5 md:w-6 md:h-6" />
                  Visit the Store
                  <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 group-hover:rotate-45 transition-transform duration-500" />
                </a>
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">
                  andromis-2.myshopify.com
                </span>
              </div>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7" data-testid="merch-grid">
            {MERCH.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.12, ease: [0.2, 0.7, 0.2, 1] }}
                className="group relative block"
                data-testid={`merch-item-${m.id}`}
              >
                {(() => {
                  const CardInner = (
                    <>
                      <div className="relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/5] bg-gradient-to-b from-[#0a1220] via-[#060a12] to-[#04060a]">
                        {/* Spotlight */}
                        <div
                          className="absolute inset-0 opacity-70 transition-opacity duration-700 group-hover:opacity-100 pointer-events-none"
                          style={{
                            background:
                              "radial-gradient(circle at 50% 40%, rgba(79,168,224,0.28) 0%, rgba(79,168,224,0.08) 30%, transparent 65%)",
                          }}
                        />
                        {/* Chrome grid */}
                        <div
                          className="absolute inset-0 opacity-[0.06] pointer-events-none"
                          style={{
                            backgroundImage:
                              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                            backgroundSize: "48px 48px",
                          }}
                        />
                        {/* Clipped frame */}
                        <div className="absolute inset-6 md:inset-8 border border-white/8 rounded-xl pointer-events-none" />

                        {/* Chapter number */}
                        <div className="absolute top-5 left-5 h-display text-xs tracking-[0.3em] text-[#9bd1f5]/80 z-10">
                          0{i + 1} / 0{MERCH.length}
                        </div>
                        <div className="absolute bottom-4 left-4 h-display text-[10px] tracking-[0.3em] text-white/60 uppercase z-10 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/10">
                          {m.kind}
                        </div>

                        <motion.img
                          src={m.src}
                          alt={m.name}
                          className="absolute inset-0 w-full h-full object-cover"
                          whileHover={{ scale: 1.06 }}
                          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
                        />

                        {/* Bottom shade for readability of top labels */}
                        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#04060a]/60 to-transparent pointer-events-none" />

                        {/* BIG availability badge — top-right overlay */}
                        {m.href ? (
                          <div
                            className="absolute top-4 right-4 z-20 flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-emerald-950 h-display text-[11px] md:text-xs tracking-[0.2em] uppercase font-semibold shadow-[0_10px_30px_rgba(16,185,129,0.45),0_0_0_2px_rgba(16,185,129,0.35)]"
                            data-testid={`merch-badge-${m.id}`}
                          >
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-950 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-950" />
                            </span>
                            Available Now
                          </div>
                        ) : (
                          <div
                            className="absolute top-4 right-4 z-20 flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 text-amber-950 h-display text-[11px] md:text-xs tracking-[0.2em] uppercase font-semibold shadow-[0_10px_30px_rgba(245,158,11,0.4),0_0_0_2px_rgba(245,158,11,0.3)]"
                            data-testid={`merch-badge-${m.id}`}
                          >
                            <span className="w-2 h-2 rounded-full bg-amber-950" />
                            Coming Soon
                          </div>
                        )}
                      </div>

                      {/* Info block below image */}
                      <div className="mt-5">
                        <div className="h-display text-white text-xl md:text-2xl tracking-[-0.01em] leading-tight">
                          {m.name}
                        </div>
                        <p className="text-white/50 text-sm mt-2 leading-relaxed">
                          {m.accent}
                        </p>
                        {m.href && (
                          <div
                            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 h-display text-sm md:text-base font-bold tracking-[0.08em] uppercase px-6 py-3.5 rounded-full transition-all duration-300 shadow-[0_10px_30px_rgba(16,185,129,0.35),inset_0_1px_0_rgba(255,255,255,0.35)] group-hover:shadow-[0_16px_40px_rgba(16,185,129,0.5),inset_0_1px_0_rgba(255,255,255,0.4)] group-hover:-translate-y-0.5"
                            data-testid={`merch-cta-${m.id}`}
                          >
                            <span aria-hidden="true">🛒</span>
                            {m.href.includes("amazon") ? "Buy on Amazon" : "Buy on Shopify"}
                            <ArrowUpRight className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    </>
                  );
                  return m.href ? (
                    <a
                      href={m.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4FA8E0]/60 rounded-2xl"
                      data-testid={`merch-link-${m.id}`}
                    >
                      {CardInner}
                    </a>
                  ) : (
                    CardInner
                  );
                })()}
              </motion.div>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-12 flex items-center gap-4">
              <div className="hairline flex-1 opacity-40" />
              <p className="text-white/40 text-xs tracking-[0.28em] uppercase">
                Available on Shopify · Visit the full store
              </p>
              <div className="hairline flex-1 opacity-40" />
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* CONTACT */}
      <section
        id="contact"
        className="relative py-28 md:py-36 px-6 md:px-10 border-t border-white/5"
        data-testid="contact-section"
      >
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <div className="eyebrow mb-5 flex items-center justify-center gap-3">
              <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
              Get in touch
              <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
            </div>
            <h2
              className="h-display text-5xl md:text-7xl text-white mb-8 tracking-[-0.03em] leading-[0.95]"
              data-testid="contact-title"
            >
              Reach out to <br />
              <span className="italic font-light text-white/60">Andromis Games.</span>
            </h2>
            <p className="text-white/55 max-w-xl mx-auto mb-12 leading-relaxed" data-testid="contact-body">
              Send us a message. We’ll get back to you.
            </p>
            <a href="mailto:miky@andromisgames.com" className="btn-primary" data-testid="contact-email-btn">
              <Mail className="w-4 h-4" />
              miky@andromisgames.com
            </a>
          </Reveal>

          <div className="mt-24 pt-10 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Andromis" className="h-10 w-auto object-contain max-w-[140px]" />
              <span className="text-white/40 text-xs tracking-[0.3em] uppercase">LLC</span>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <a
                href="/privacy-policy"
                className="text-white/50 hover:text-white text-xs tracking-[0.2em] uppercase transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </a>
              <a
                href="/delete-account"
                className="text-white/50 hover:text-white text-xs tracking-[0.2em] uppercase transition-colors"
                data-testid="footer-delete-account-link"
              >
                Delete Account
              </a>
              <div className="text-white/30 text-xs tracking-[0.2em] uppercase">
                © {new Date().getFullYear()} Andromis Games LLC · All rights reserved
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE LIGHTBOX */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent
          className="max-w-[95vw] md:max-w-6xl bg-[#04060a]/95 border border-white/10 p-0 overflow-hidden"
          data-testid="lightbox-dialog"
        >
          {lightbox && (
            <div className="relative">
              <img
                src={lightbox.src}
                alt={lightbox.caption}
                className="w-full h-auto max-h-[85vh] object-contain bg-black"
                data-testid="lightbox-image"
              />
              {lightbox.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#9bd1f5] mb-1">Gameplay Still</div>
                  <div className="text-white/90 text-sm">{lightbox.caption}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
